import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { Button } from "../components/ui/Button";
import slaService from "../services/slaService";
import { Clock } from "lucide-react";

const PRIORITY_BADGE = {
  Urgente: "bg-red-100 text-red-700",
  Alta: "bg-orange-100 text-orange-700",
  Media: "bg-blue-100 text-blue-700",
  Baixa: "bg-gray-100 text-gray-600",
};
const fmtH = (mins) => {
  const m = Number(mins || 0);
  if (!m) return "—";
  const h = m / 60;
  return Number.isInteger(h) ? `${h}h` : `${h.toFixed(1)}h`;
};

const Sla = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const activeTenantName = useMemo(
    () => localStorage.getItem("active_tenant_name") || "",
    []
  );

  useEffect(() => {
    slaService
      .list()
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => setError(e.response?.data?.detail || "Erro ao carregar SLA."))
      .finally(() => setLoading(false));
  }, []);

  const setField = (priority, field, value) => {
    setMessage("");
    setRows((prev) =>
      prev.map((r) => (r.priority === priority ? { ...r, [field]: value } : r))
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const payload = rows.map((r) => ({
        priority: r.priority,
        first_response_mins: Math.max(0, Number(r.first_response_mins || 0)),
        resolution_mins: Math.max(0, Number(r.resolution_mins || 0)),
        is_active: r.is_active !== false,
      }));
      const saved = await slaService.save(payload);
      setRows(Array.isArray(saved) ? saved : rows);
      setMessage("Metas de SLA salvas com sucesso.");
    } catch (e) {
      setError(e.response?.data?.detail || "Erro ao salvar SLA.");
    } finally {
      setSaving(false);
    }
  };

  const handleScan = async () => {
    try {
      setScanning(true);
      setError("");
      setMessage("");
      const res = await slaService.scanEscalations();
      const n = res?.alerted ?? 0;
      setMessage(
        n > 0
          ? `Verificação concluída: ${n} chamado(s) em risco/violado escalonado(s) (responsável + gerentes notificados).`
          : "Verificação concluída: nenhum chamado em risco/violado no momento."
      );
    } catch (e) {
      setError(e.response?.data?.detail || "Erro ao verificar SLA.");
    } finally {
      setScanning(false);
    }
  };

  const numInput =
    "w-28 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">SLA</h1>
            <p className="text-gray-500">
              Metas de atendimento por prioridade (horário comercial)
              {activeTenantName ? ` — ${activeTenantName}` : ""}.
            </p>
          </div>
          <Button type="button" variant="outline" onClick={handleScan} disabled={scanning}
            className="h-auto gap-2 px-3 py-2 text-gray-700">
            <Clock size={16} /> {scanning ? "Verificando..." : "Verificar SLA agora"}
          </Button>
        </div>

        <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          Tempos em <strong>minutos</strong>. <strong>1ª resposta</strong> = prazo p/ o primeiro
          retorno ao solicitante; <strong>Resolução</strong> = prazo p/ encerrar o chamado. Os
          relatórios usam estes valores p/ calcular o % de cumprimento.
        </div>

        {message && (
          <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-green-700">{message}</div>
        )}
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700">{error}</div>
        )}

        <div className="rounded-lg border bg-white overflow-x-auto">
          {loading ? (
            <div className="p-6 text-gray-500">Carregando...</div>
          ) : (
            <>
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-600">
                    <th className="px-4 py-3 font-semibold">Prioridade</th>
                    <th className="px-4 py-3 font-semibold">1ª resposta (min)</th>
                    <th className="px-4 py-3 font-semibold">Resolução (min)</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.priority} className="border-b last:border-0">
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${PRIORITY_BADGE[r.priority] || "bg-gray-100 text-gray-600"}`}>
                          {r.priority}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          min="0"
                          className={numInput}
                          value={r.first_response_mins}
                          onChange={(e) => setField(r.priority, "first_response_mins", e.target.value)}
                        />
                        <span className="ml-2 text-xs text-gray-400">= {fmtH(r.first_response_mins)}</span>
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          min="0"
                          className={numInput}
                          value={r.resolution_mins}
                          onChange={(e) => setField(r.priority, "resolution_mins", e.target.value)}
                        />
                        <span className="ml-2 text-xs text-gray-400">= {fmtH(r.resolution_mins)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center justify-between gap-3 p-4">
                <p className="flex items-center gap-1.5 text-xs text-gray-400">
                  <Clock size={14} /> Contado em horário comercial quando há expediente configurado (cai p/ 24×7 se não houver).
                </p>
                <Button type="button" onClick={handleSave} disabled={saving}>
                  {saving ? "Salvando..." : "Salvar metas"}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Sla;
